/**
 * The catalog behind the drilldown's Admin tab: every place on the Loan
 * Drilldown that shows, hides, or locks something based on WHO is signed in,
 * with the rule in plain words and a live check against the current user.
 *
 * This is a hand-maintained mirror of the gates in code — there is no central
 * permission registry to read it from. When you add, change or remove a
 * permission check anywhere on the drilldown, update the matching entry here
 * (each entry names the file that owns the real gate in `source`).
 *
 * ROLE_SIGNALS — the building blocks (Admin, Underwriter, Capital markets, …):
 *   { key, label, resolvedBy, backend, has(ctx) }
 *
 * GATED_AREAS — one row per gated surface:
 *   { id, tab, location, kind, who[], rule, enforced, enforcedNote, source,
 *     allowed(ctx), shownHere?(ctx) }
 *   - `allowed`   — does THIS user pass the permission part of the rule?
 *   - `shownHere` — optional loan/pipeline half of the rule (product, pipeline
 *                   capability, status). False = the permission may pass but the
 *                   area doesn't appear on this particular loan.
 *   - `enforced`  — "server" when the backend also refuses the call, "screen"
 *                   when only the UI hides it, "none" when nothing is gated.
 *
 * `ctx` is built per user by `buildUserContext` in the tab — once for the
 * signed-in user (the "You" column) and once per user from GET /users/all_users
 * (the "Users" column). Rules must therefore read only `ctx`, never a hook.
 */

export const KIND = {
  TAB: "Tab",
  ACTION: "Action",
  PANEL: "Panel",
  EDIT: "Edit access",
  VIEW: "View",
};

// The Admin tab renders one table per kind, in this order.
export const KIND_ORDER = [KIND.TAB, KIND.ACTION, KIND.PANEL, KIND.EDIT, KIND.VIEW];

export const ENFORCED = {
  SERVER: "server",
  SCREEN: "screen",
  NONE: "none",
};

// Role labels used in the "Who" chips. Keep in step with ROLE_SIGNALS labels.
const R = {
  ADMIN: "Admin",
  SUPER: "Super admin",
  UW: "Underwriter",
  CM: "Capital markets",
  CLOSER: "Closer",
  ASSIGNED_CLOSER: "Assigned closer",
  HIGH_LEVEL: "High-level user",
  UW_GROUP: "Underwriting group",
  CHAT: "Chat allowlist",
  EVERYONE: "Everyone",
  NOBODY: "Nobody",
  ADMIN_ROSTER: "Admin roster",
  UW_ROSTER: "UW / Admin roster",
  SALES: "Sales / Loan officer",
  PROCESSING: "Processing",
};

// Everyone who gets the Loan Lock tab (usePermissionsHook `canViewLoanLock`).
const LOCK_VIEWERS = [R.UW, R.ADMIN, R.CM, R.SALES, R.PROCESSING, R.CLOSER];

// Which users count as each "Who" label — used to split a row's user list into
// one section per label. A user who passes a row's rule shows under every label
// they match; one who passes through a rule-specific clause (a partner build,
// a loan condition) and matches none lands in "Other".
export const WHO_MATCHERS = {
  [R.ADMIN]: (c) => c.isAdmin,
  [R.SUPER]: (c) => c.isSuperAdmin,
  [R.UW]: (c) => c.isUnderwriter,
  [R.CM]: (c) => c.isCapitalMarkets,
  [R.CLOSER]: (c) => c.isCloserAny,
  [R.ASSIGNED_CLOSER]: (c) => c.isAssignedCloser,
  [R.HIGH_LEVEL]: (c) => c.isHighLevelUser,
  [R.UW_GROUP]: (c) => c.canEditUwNotes,
  [R.CHAT]: (c) => c.canSeeChat,
  [R.EVERYONE]: () => true,
  [R.NOBODY]: () => false,
  [R.ADMIN_ROSTER]: (c) => c.isAdminRoster,
  [R.UW_ROSTER]: (c) => c.isUwOrAdminRoster,
  [R.SALES]: (c) => c.isSalesOrLoanOfficer,
  [R.PROCESSING]: (c) => c.isProcessor,
};

export const ROLE_SIGNALS = [
  {
    key: "admin",
    label: R.ADMIN,
    resolvedBy:
      "The user carries the `admin` permission, or is a super admin (super admins always count as admins).",
    backend: "Backend admin checks read `admin` in the user's roles or permissions.",
    has: (c) => c.isAdmin,
  },
  {
    key: "superAdmin",
    label: R.SUPER,
    resolvedBy:
      "An email allowlist in the frontend. There is no super-admin permission on the user record yet.",
    backend: "Not known to the backend — screen-only.",
    has: (c) => c.isSuperAdmin,
  },
  {
    key: "underwriter",
    label: R.UW,
    resolvedBy: "The user carries the `underwriting` permission.",
    backend: "Same permission on the user record.",
    has: (c) => c.isUnderwriter,
  },
  {
    key: "capitalMarkets",
    label: R.CM,
    resolvedBy:
      "An email allowlist in the frontend, or the `capital_markets` team on the user record.",
    backend:
      "Backend checks the `capital_markets` team only (admins bypass). Someone on the allowlist without the team tag sees the buttons but gets a 403.",
    has: (c) => c.isCapitalMarkets,
  },
  {
    key: "closer",
    label: R.CLOSER,
    resolvedBy:
      "An email allowlist in the frontend, or the `closing` team on the user record. The Summary's Closer view and the Loan Lock tab also accept the Closers loan-role roster.",
    backend: "Backend closing checks use the Closers group / roster, plus admins.",
    has: (c) => c.isCloserAny,
  },
  {
    key: "sales",
    label: R.SALES,
    resolvedBy:
      "The `sales` or `sales_admin` permission, or the `loan_officer` / `sales_admin` role.",
    backend:
      "The backend's ported roles were mass-defaulted to loan_officer, so this matches most users.",
    has: (c) => c.isSalesOrLoanOfficer,
  },
  {
    key: "processing",
    label: R.PROCESSING,
    resolvedBy:
      "The `processor` role, a `processor` / `processing` team or permission, or the Processor loan-role roster.",
    backend: "Screen-only.",
    has: (c) => c.isProcessor,
  },
  {
    key: "assignedCloser",
    label: R.ASSIGNED_CLOSER,
    resolvedBy: "This loan's Loan Closer field is the signed-in user's email. Per loan.",
    backend: "Screen-only.",
    has: (c) => c.isAssignedCloser,
  },
  {
    key: "uwGroup",
    label: R.UW_GROUP,
    resolvedBy:
      "Admin, or the `underwriting` permission, or membership in an org group whose name / related roles mention underwriting or Pre-UW, or being on the Underwriter / Junior Underwriter loan-role roster.",
    backend: "Screen-only (Eastview policy).",
    has: (c) => c.canEditUwNotes,
  },
  {
    key: "highLevel",
    label: R.HIGH_LEVEL,
    resolvedBy: "A short hardcoded email list of internal developers / ops leads.",
    backend: "Screen-only.",
    has: (c) => c.isHighLevelUser,
  },
  {
    key: "chat",
    label: R.CHAT,
    resolvedBy: "A development build, or an email on the loan-chat trial allowlist.",
    backend: "Screen-only.",
    has: (c) => c.canSeeChat,
  },
  {
    key: "borrowerPortal",
    label: "Borrower portal access",
    resolvedBy:
      "Everyone EXCEPT users whose email domain is on the restricted list (currently maratonacapital.com).",
    backend: "Screen-only.",
    has: (c) => c.canAccessBorrowerPortal,
  },
];

const isDscr = (c) => c.loan?.loanProductType === "DSCR";
const lockOnThisLoan = (c) =>
  !!c.capabilities?.loanLock && (!c.isAcquisitions || isDscr(c));
// Eastview builds are neither Resfin nor Lendmarq, so this collapses to the
// underwriting permission here; the partner branches are listed for accuracy.
const uwTabsAllowed = (c) =>
  (c.isUnderwriter || c.isResfin || c.isLendmarq) && !c.isResilender;

export const GATED_AREAS = [
  // ── Tabs ────────────────────────────────────────────────────────────────
  {
    id: "admin-tab",
    tab: "Admin",
    location: "Tab in the drilldown nav",
    kind: KIND.TAB,
    who: [R.ADMIN, R.SUPER],
    rule: "Shown only to admins and super admins. The route is gated too, so a pasted link does nothing for anyone else.",
    enforced: ENFORCED.SCREEN,
    enforcedNote: "Nav entry and route both check the admin flag.",
    source: "LoanDrilldownScreen.js",
    allowed: (c) => c.isAdmin,
  },
  {
    id: "loan-lock-tab",
    tab: "Loan Lock",
    location: "Tab in the drilldown nav",
    kind: KIND.TAB,
    who: LOCK_VIEWERS,
    rule: "Shown to underwriters, admins, capital markets, sales / loan officers, processing and closers. Pre-close: every product. Acquisitions: DSCR loans only. Anyone else who opens the link sees a no-access message.",
    enforced: ENFORCED.SCREEN,
    enforcedNote: "The lock reads are not role-gated on the backend; only confirm/return/cancel/extension are (see the Loan Lock actions row).",
    source: "LoanDrilldownScreen.js · LoanDrilldownLoanLockTabV2.js",
    allowed: (c) => c.canViewLoanLock,
    shownHere: lockOnThisLoan,
  },
  {
    id: "loan-lock-manage",
    tab: "Loan Lock",
    location: "Confirm · Return for action · Apply extension",
    kind: KIND.ACTION,
    who: [R.CM],
    rule: "Only the capital markets desk can confirm a requested lock, return it, or apply (confirm / deny) an extension. Everyone who can see the tab can request a lock, request an extension, or cancel a confirmed lock from Manage lock.",
    enforced: ENFORCED.SERVER,
    enforcedNote:
      "Backend requires the `capital_markets` team OR admin. The screen shows these buttons to capital markets only, so an admin without the desk tag is allowed by the server but has no button.",
    source: "LoanLockFormV2.js · pricing.py",
    allowed: (c) => c.isCapitalMarkets,
    shownHere: lockOnThisLoan,
  },
  {
    id: "post-close-lock",
    tab: "Loan Lock",
    location: "Post-close lock panel (acquisitions)",
    kind: KIND.VIEW,
    who: LOCK_VIEWERS,
    rule: "On acquisition DSCR loans the post-close lock panel is readable by everyone who gets the Loan Lock tab. Request / confirm / cancel open in the Loan Lock sidebar. Confirm stays capital-markets only; anyone can cancel a confirmed lock.",
    enforced: ENFORCED.SCREEN,
    source: "PostCloseLockPanel.js · DrilldownSidebarPostCloseLock.js",
    allowed: (c) => c.canViewLoanLock,
    shownHere: (c) => c.isAcquisitions && isDscr(c),
  },
  {
    id: "wire-funding-tab",
    tab: "Wire & Funding",
    location: "Tab in the drilldown nav",
    kind: KIND.TAB,
    who: [R.UW],
    rule: "Eastview: underwriters only — an admin without the underwriting permission does not get this tab. (Resfin / Lendmarq builds show it to everyone; Resilender never.)",
    enforced: ENFORCED.SCREEN,
    source: "LoanDrilldownScreen.js",
    allowed: uwTabsAllowed,
  },
  {
    id: "wire-statement-lock",
    tab: "Wire & Funding",
    location: "Funding statement → Lock button",
    kind: KIND.ACTION,
    who: [R.CLOSER],
    rule: "Only closers see Lock, and only while the statement is still a Projection.",
    enforced: ENFORCED.SCREEN,
    enforcedNote: "The statement lock endpoint does not check the caller's role.",
    source: "StatementPane.js",
    allowed: (c) => c.isCloser,
  },
  {
    id: "checklist-tab",
    tab: "Checklist",
    location: "Tab in the drilldown nav",
    kind: KIND.TAB,
    who: [R.UW],
    rule: "Same rule as Wire & Funding (underwriters on Eastview), and pre-close loans only.",
    enforced: ENFORCED.SCREEN,
    source: "LoanDrilldownScreen.js",
    allowed: uwTabsAllowed,
    shownHere: (c) => !!c.capabilities?.precloseChecklist,
  },
  {
    id: "ai-review-tab",
    tab: "AI Review",
    location: "Tab in the drilldown nav (incl. Loan Opening field changes + revert)",
    kind: KIND.TAB,
    who: [R.ADMIN],
    rule: "Admins only — running agents, reviewing their output and reverting agent-written fields.",
    enforced: ENFORCED.SERVER,
    enforcedNote: "Every /admin/loans/{id}/ai-reviews endpoint requires admin.",
    source: "LoanDrilldownScreen.js · AiReviewTab.js",
    allowed: (c) => c.isAdmin,
  },
  {
    id: "chat-tab",
    tab: "Chat",
    location: "Tab in the drilldown nav + floating chat bubble",
    kind: KIND.TAB,
    who: [R.CHAT],
    rule: "Trial feature: visible in development builds, or to emails on the loan-chat allowlist. Nav, route and bubble all use the same check.",
    enforced: ENFORCED.SCREEN,
    source: "loanChatAccess.js",
    allowed: (c) => c.canSeeChat,
  },

  // ── Actions & panels inside tabs ────────────────────────────────────────
  {
    id: "ai-conditions",
    tab: "Conditions",
    location: "AI suggested conditions panel",
    kind: KIND.PANEL,
    who: [R.ADMIN],
    rule: "Admins only. Also hidden once someone chooses to ignore AI suggestions (bring it back from AI Review).",
    enforced: ENFORCED.SERVER,
    enforcedNote: "Promote / reject go through the admin AI endpoints.",
    source: "LoanDrilldownConditionsChecklistTab.js",
    allowed: (c) => c.isAdmin,
  },
  {
    id: "ai-exceptions",
    tab: "Exceptions",
    location: "AI suggested exceptions panel",
    kind: KIND.PANEL,
    who: [R.ADMIN],
    rule: "Admins only. Hidden when AI suggested exceptions are ignored.",
    enforced: ENFORCED.SERVER,
    source: "LoanDrilldownExceptionsTab.js",
    allowed: (c) => c.isAdmin,
  },
  {
    id: "collateral-search",
    tab: "Checklist / Closed Package",
    location: "Check for package in Sourcepoint",
    kind: KIND.ACTION,
    who: [R.ADMIN],
    rule: "Admins only. Lives on the pre-close Checklist and the acquisitions Closed Package tab.",
    enforced: ENFORCED.SERVER,
    enforcedNote: "Collateral search run endpoint requires admin.",
    source: "CollateralSearchAction.js",
    allowed: (c) => c.isAdmin,
  },
  {
    id: "closing-ai-call",
    tab: "Closing / Settlement",
    location: "Readiness → AI readiness call",
    kind: KIND.ACTION,
    who: [R.ADMIN],
    rule: "Admins only can queue the AI closing-readiness review. Everyone can read the result.",
    enforced: ENFORCED.SERVER,
    source: "LoanClosingSubTabReadiness.js",
    allowed: (c) => c.isAdmin,
  },
  {
    id: "closing-schedule-edit",
    tab: "Closing / Settlement",
    location: "Readiness → Edit schedule (also in the closing sidebar)",
    kind: KIND.EDIT,
    who: [R.ADMIN, R.CLOSER, R.CM],
    rule: "Admins, closers and capital markets can change closing / settlement dates, statuses and the loan closer. The button disappears once the loan is closed.",
    enforced: ENFORCED.SERVER,
    enforcedNote: "PUT /closing/schedule requires capital markets or a closing-team member (Closers group / roster / admin).",
    source: "LoanClosingSubTabReadiness.js · ClosingDetailDrawer.js · closing.py",
    allowed: (c) => c.isAdmin || c.isCloser || c.isCapitalMarkets,
  },
  {
    id: "loan-info-reveal",
    tab: "Loan Info",
    location: "Show hidden fields toggle",
    kind: KIND.EDIT,
    who: [R.SUPER],
    rule: "Super admins only. Revealing a conditionally-hidden field also makes it editable, so regular admins are deliberately excluded. A saved preference is ignored for anyone who isn't a super admin.",
    enforced: ENFORCED.SCREEN,
    source: "LoanDrilldownLoanInfoRedesignTab.js",
    allowed: (c) => c.isSuperAdmin,
  },
  {
    id: "loan-info-uw-fields",
    tab: "Loan Info",
    location: "Underwriting-only fields (new view)",
    kind: KIND.EDIT,
    who: [R.EVERYONE],
    rule: "Open to everyone. The underwriter/admin edit gate is switched off because the model's edit permissions are advisory and never enforced by the backend.",
    enforced: ENFORCED.NONE,
    source: "LoanDrilldownLoanInfoRedesignTab.js",
    allowed: () => true,
  },
  {
    id: "loan-info-classic-uw",
    tab: "Loan Info",
    location: "Underwriting-only fields (classic view)",
    kind: KIND.EDIT,
    who: [R.UW_ROSTER],
    rule: "In the classic view, underwriting fields are editable only for emails on the Underwriter or Admin loan-role rosters (not the permission flags).",
    enforced: ENFORCED.SCREEN,
    source: "LoanDrilldownDetailsTab.js",
    allowed: (c) => c.isUwOrAdminRoster,
  },
  {
    id: "summary-closer-view",
    tab: "Summary",
    location: "View selector → Closer view",
    kind: KIND.VIEW,
    who: [R.ADMIN, R.CLOSER],
    rule: "Admins and closers can switch to the Closer view (closers = the closer flag or the Closers roster). Closers land on it by default until they pick a view.",
    enforced: ENFORCED.SCREEN,
    source: "useLoanSummaryView.js",
    allowed: (c) => c.isAdmin || c.isCloser || c.isCloserRoster,
  },
  {
    id: "summary-assign-self",
    tab: "Summary",
    location: "Assigned team → role dropdowns",
    kind: KIND.ACTION,
    who: [R.ADMIN_ROSTER],
    rule: "Emails on the Admin loan-role roster get themselves appended to every role dropdown, so they can assign themselves to any role.",
    enforced: ENFORCED.SCREEN,
    source: "summaryShared.js",
    allowed: (c) => c.isAdminRoster,
  },
  {
    id: "summary-permitted-users",
    tab: "Summary",
    location: "Team card → permitted users",
    kind: KIND.EDIT,
    who: [R.EVERYONE],
    rule: "Not gated on screen — anyone who can open the loan can add or remove permitted users.",
    enforced: ENFORCED.NONE,
    source: "summaryBento.js",
    allowed: () => true,
  },
  {
    id: "gc-experience",
    tab: "Experience Calc.",
    location: "GC qualifying experience → apply / edit",
    kind: KIND.EDIT,
    who: [R.ADMIN, R.UW],
    rule: "Admins and underwriters can apply or override the general contractor's qualifying experience. Everyone else reads it.",
    enforced: ENFORCED.SCREEN,
    source: "GcQualifyingExperienceCard.js",
    allowed: (c) => c.isAdmin || c.isUnderwriter,
  },
  {
    id: "invite-to-portal",
    tab: "Applicants",
    location: "Applicant card + borrower sidebar → Invite to Portal",
    kind: KIND.ACTION,
    who: [R.EVERYONE],
    rule: "Everyone except users from a restricted email domain (currently maratonacapital.com).",
    enforced: ENFORCED.SCREEN,
    source: "ApplicantDetailActions.js · LoanBorrowerSidebarActions.js",
    allowed: (c) => c.canAccessBorrowerPortal,
  },
  {
    id: "office-web-view",
    tab: "Documents",
    location: "File preview → Open in Microsoft Office for the web",
    kind: KIND.ACTION,
    who: [R.NOBODY],
    rule: "Turned off for everyone until the backend's short-lived file link endpoint ships. When re-enabled: development builds + an email allowlist, Excel files only.",
    enforced: ENFORCED.SCREEN,
    source: "officeWebViewAccess.js",
    allowed: (c) => c.canSeeOfficeView,
  },

  // ── Right rail & sidebars ───────────────────────────────────────────────
  {
    id: "rail-uw-buttons",
    tab: "Right rail",
    location: "Underwriting Notes · Processing Notes · UW Checklist buttons",
    kind: KIND.VIEW,
    who: [R.UW, R.HIGH_LEVEL],
    rule: "Shown to underwriters and high-level users (and every user on Resfin builds). Admins without the underwriting permission do not get these buttons. UW Checklist is also hidden on Resfin.",
    enforced: ENFORCED.SCREEN,
    source: "LoanDrilldownScreenSidebar.js",
    allowed: (c) => c.isUnderwriter || c.isResfin || c.isHighLevelUser,
  },
  {
    id: "uw-note-edit",
    tab: "Notes",
    location: "Underwriting note (edit)",
    kind: KIND.EDIT,
    who: [R.UW_GROUP],
    rule: "Editable by admins and the underwriting groups (permission, UW org group, or Underwriter / Junior Underwriter roster). Everyone else sees it read-only. The Processing note stays editable for all.",
    enforced: ENFORCED.SCREEN,
    source: "useCanEditUnderwritingNotes.js · LoanNotesPane.js",
    allowed: (c) => c.canEditUwNotes,
  },
  {
    id: "closing-sidebar",
    tab: "Right rail",
    location: "Closing / Settlement readiness sidebar",
    kind: KIND.PANEL,
    who: [R.ADMIN, R.SUPER, R.CLOSER, R.ASSIGNED_CLOSER],
    rule: "Admins, super admins, closers, and the loan's own assigned closer. The rail button doesn't render for anyone else.",
    enforced: ENFORCED.SCREEN,
    source: "ClosingSidebarButton.js",
    allowed: (c) => c.isAdmin || c.isSuperAdmin || c.isCloser || c.isAssignedCloser,
  },
  {
    id: "quick-add-lock",
    tab: "Right rail",
    location: "Quick Add → Rate lock",
    kind: KIND.ACTION,
    who: LOCK_VIEWERS,
    rule: "Same rule as the Loan Lock tab: underwriters, admins, capital markets, sales / loan officers, processing and closers; on acquisitions, DSCR only.",
    enforced: ENFORCED.SCREEN,
    source: "addToLoanCatalog.js",
    allowed: (c) => c.canViewLoanLock,
    shownHere: (c) => !c.isAcquisitions || isDscr(c),
  },
];
